import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, ShieldCheck, GitPullRequestArrow, PiggyBank, TrendingUp, LayoutTemplate, UserCog, Lock, ScrollText, Settings, LogOut, Bell } from "lucide-react";
import Logo from "@/components/layout/Logo";
import ThemeToggle from "@/components/widgets/ThemeToggle";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/customers", icon: Users, label: "Customers" },
  { to: "/admin/loans", icon: ClipboardList, label: "Loan Applications" },
  { to: "/admin/kyc", icon: ShieldCheck, label: "KYC Review" },
  { to: "/admin/underwriting", icon: GitPullRequestArrow, label: "Underwriting" },
  { to: "/admin/disbursement", icon: PiggyBank, label: "Disbursement" },
  { to: "/admin/repayment", icon: TrendingUp, label: "Repayment Tracking" },
  { to: "/admin/reports", icon: TrendingUp, label: "Reports" },
  { to: "/admin/cms", icon: LayoutTemplate, label: "CMS" },
  { to: "/admin/users", icon: UserCog, label: "User Management" },
  { to: "/admin/roles", icon: Lock, label: "Roles & Permissions" },
  { to: "/admin/audit", icon: ScrollText, label: "Audit Logs" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr] bg-background">
      <aside className="hidden lg:flex flex-col border-r border-border/60 bg-card/40" data-testid="admin-sidebar">
        <div className="px-6 py-5 border-b border-border/60 flex items-center justify-between">
          <Logo />
          <span className="text-[10px] px-2 py-0.5 rounded-full brand-gradient text-white font-semibold uppercase tracking-widest">Admin</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end} className={({isActive}) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${isActive ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted"}`} data-testid={`admin-side-${label.toLowerCase().replace(/\s+/g,"-").replace(/&/g,"and")}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border/60 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full brand-gradient flex items-center justify-center text-white font-display font-semibold">A</div>
          <div className="text-sm flex-1 min-w-0">
            <div className="font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onLogout} data-testid="admin-logout"><LogOut className="h-4 w-4" /></Button>
        </div>
      </aside>

      <div className="flex flex-col min-w-0">
        <header className="h-16 border-b border-border/60 flex items-center justify-between px-6 glass sticky top-0 z-20">
          <div className="lg:hidden"><Logo compact /></div>
          <div className="hidden lg:flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">Admin Console</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">Live</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="icon"><Bell className="h-4 w-4" /></Button>
            <Link to="/app"><Button variant="outline" size="sm">Customer View</Button></Link>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10 max-w-none w-full">{children}</main>
      </div>
    </div>
  );
}
